/**
 * Client-side PNG export of mock criterion scores (no server work).
 * Copy is in English for downloads / sharing.
 */

export interface MockChartCriterion {
  label: string;
  score: number;
}

export interface MockChartExportInput {
  criteria: MockChartCriterion[];
  dialogueName: string;
  categoryName?: string;
  overallScore: number;
  submissionId: number;
  submittedAt?: string;
  /** Fires after the download is triggered (or if export fails). */
  onComplete?: () => void;
}

const MAX_SCORE = 5;

function clampScore(s: number): number {
  if (Number.isNaN(s)) return 0;
  return Math.min(MAX_SCORE, Math.max(0, s));
}

/**
 * Renders a horizontal bar chart and triggers a PNG download in the browser.
 */
export function downloadMockResultBarChartPng(input: MockChartExportInput): void {
  const { criteria, dialogueName, categoryName, overallScore, submissionId, submittedAt, onComplete } = input;
  if (!criteria.length) {
    onComplete?.();
    return;
  }

  const W = 900;
  const rowH = 46;
  const topH = 108;
  const bottomH = 76;
  const H = topH + criteria.length * rowH + bottomH;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    onComplete?.();
    return;
  }
  ctx.scale(dpr, dpr);

  // Light background (readable when shared)
  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#1a1a1a';
  ctx.font = 'bold 22px system-ui, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('Mock test performance by criterion', 24, 38);

  ctx.fillStyle = '#444';
  ctx.font = '14px system-ui, "Segoe UI", Roboto, sans-serif';
  let lineY = 60;
  ctx.fillText(`Dialogue: ${dialogueName}`, 24, lineY);
  lineY += 18;
  if (categoryName) {
    ctx.fillText(`Category: ${categoryName}`, 24, lineY);
    lineY += 18;
  }
  ctx.fillText('Score scale: 1 (needs improvement) — 5 (strong)', 24, lineY);
  lineY += 18;
  ctx.fillText('Each bar shows your average for that criterion across graded audio items.', 24, lineY);

  const barLeft = 272;
  const barRight = W - 72;
  const barMaxW = barRight - barLeft;

  criteria.forEach((c, i) => {
    const y = topH + i * rowH + 6;
    const score = clampScore(c.score);

    ctx.fillStyle = '#212529';
    ctx.font = '13px system-ui, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const maxLabel = 42;
    const label =
      c.label.length > maxLabel ? `${c.label.slice(0, maxLabel - 1)}…` : c.label;
    ctx.fillText(label, barLeft - 14, y + rowH / 2 - 2);

    ctx.fillStyle = '#dee2e6';
    ctx.fillRect(barLeft, y + 8, barMaxW, 22);

    const w = (score / MAX_SCORE) * barMaxW;
    const grad = ctx.createLinearGradient(barLeft, 0, barLeft + Math.max(w, 1), 0);
    grad.addColorStop(0, '#E63946');
    grad.addColorStop(1, '#9d0214');
    ctx.fillStyle = grad;
    ctx.fillRect(barLeft, y + 8, w, 22);

    ctx.fillStyle = '#1a1a1a';
    ctx.font = 'bold 13px system-ui, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(score.toFixed(2), barLeft + barMaxW + 10, y + 19);
  });

  const yRule = topH + criteria.length * rowH + 16;
  ctx.strokeStyle = '#ced4da';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(24, yRule);
  ctx.lineTo(W - 24, yRule);
  ctx.stroke();

  ctx.fillStyle = '#1a1a1a';
  ctx.font = 'bold 16px system-ui, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(`Overall average score: ${clampScore(overallScore).toFixed(2)} / 5.00`, 24, yRule + 30);

  if (submittedAt) {
    ctx.font = '12px system-ui, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#6c757d';
    const d = new Date(submittedAt);
    const dateStr = Number.isNaN(d.getTime())
      ? submittedAt
      : d.toLocaleString('en-AU', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
    ctx.fillText(`Submitted: ${dateStr}`, 24, yRule + 52);
  }

  canvas.toBlob(
    (blob) => {
      try {
        if (!blob) {
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeId = String(submissionId).replace(/\D/g, '') || 'submission';
        a.download = `mock-test-performance-${safeId}.png`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } finally {
        onComplete?.();
      }
    },
    'image/png',
    0.92
  );
}

export interface MockSegmentChartCriterion {
  label: string;
  score: number;
}

export interface MockSegmentChartBlock {
  /** 1-based label for the candidate, e.g. "Recording 1" */
  title: string;
  criteria: MockSegmentChartCriterion[];
}

export interface MockSegmentChartExportInput {
  dialogueName: string;
  segments: MockSegmentChartBlock[];
  submissionId: number;
  submittedAt?: string;
  onComplete?: () => void;
}

/**
 * Client-side PNG: one section per graded recording with criterion bars (no server work).
 */
export function downloadMockResultSegmentsBarChartPng(input: MockSegmentChartExportInput): void {
  const { dialogueName, segments, submissionId, submittedAt, onComplete } = input;
  if (!segments.length) {
    onComplete?.();
    return;
  }

  const W = 920;
  const rowH = 40;
  const segmentHeaderH = 36;
  const segmentGap = 20;
  const topH = 100;
  let bodyH = 0;
  segments.forEach((seg) => {
    bodyH += segmentHeaderH + seg.criteria.length * rowH + segmentGap;
  });
  const bottomH = 72;
  const H = topH + bodyH + bottomH;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    onComplete?.();
    return;
  }
  ctx.scale(dpr, dpr);

  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#1a1a1a';
  ctx.font = 'bold 22px system-ui, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('Mock test — performance by recording', 24, 38);

  ctx.fillStyle = '#444';
  ctx.font = '14px system-ui, "Segoe UI", Roboto, sans-serif';
  let lineY = 60;
  ctx.fillText(`Test: ${dialogueName}`, 24, lineY);
  lineY += 18;
  ctx.fillText('Scale: 1 (needs improvement) — 5 (strong). Each block is one of your recordings.', 24, lineY);

  const barLeft = 280;
  const barRight = W - 56;
  const barMaxW = barRight - barLeft;

  let yCursor = topH;

  segments.forEach((seg) => {
    ctx.fillStyle = '#212529';
    ctx.font = 'bold 15px system-ui, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(seg.title, 24, yCursor + 24);
    yCursor += segmentHeaderH;

    seg.criteria.forEach((c) => {
      const y = yCursor + 6;
      const score = clampScore(c.score);

      ctx.fillStyle = '#212529';
      ctx.font = '13px system-ui, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const maxLabel = 44;
      const label =
        c.label.length > maxLabel ? `${c.label.slice(0, maxLabel - 1)}…` : c.label;
      ctx.fillText(label, barLeft - 12, y + rowH / 2 - 2);

      ctx.fillStyle = '#dee2e6';
      ctx.fillRect(barLeft, y + 8, barMaxW, 22);

      const w = (score / MAX_SCORE) * barMaxW;
      const grad = ctx.createLinearGradient(barLeft, 0, barLeft + Math.max(w, 1), 0);
      grad.addColorStop(0, '#E63946');
      grad.addColorStop(1, '#9d0214');
      ctx.fillStyle = grad;
      ctx.fillRect(barLeft, y + 8, w, 22);

      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 13px system-ui, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(score.toFixed(0), barLeft + barMaxW + 10, y + 19);

      yCursor += rowH;
    });

    yCursor += segmentGap;
  });

  const yRule = H - bottomH + 8;
  ctx.strokeStyle = '#ced4da';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(24, yRule);
  ctx.lineTo(W - 24, yRule);
  ctx.stroke();

  if (submittedAt) {
    ctx.font = '12px system-ui, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = '#6c757d';
    ctx.textAlign = 'left';
    const d = new Date(submittedAt);
    const dateStr = Number.isNaN(d.getTime())
      ? submittedAt
      : d.toLocaleString('en-AU', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
    ctx.fillText(`Submitted: ${dateStr}`, 24, yRule + 28);
  }

  canvas.toBlob(
    (blob) => {
      try {
        if (!blob) {
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeId = String(submissionId).replace(/\D/g, '') || 'submission';
        a.download = `mock-test-by-recording-${safeId}.png`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } finally {
        onComplete?.();
      }
    },
    'image/png',
    0.92
  );
}
